import {Input as AntInput} from 'antd';
import {
  Acquirer,
  CardBrand,
  Currencies,
  Maybe,
  PaymentMethods,
  Processor,
  TokenizationMethods,
  useChargeSourcesQuery
} from 'api/generated';
import {Input, InputNumber, Select} from 'formik-antd';
import {useAccessLevel} from 'hooks/useAccessLevel';
import {useCountries} from 'hooks/useCountries';
import {
  acquirerLabels,
  billingPlans,
  cardBrandLabels,
  paymentMethodLabels,
  paymentProcessorLabels,
  tokenizationMethodLabels
} from 'lib/labels';
import {enumKeys} from 'lib/utils';
import React from 'react';
import {LabelValue} from 'types';
import {PhoneNumberInput} from './Form';
import SelectMasterAccounts from './SelectMasterAccounts';

type Props = {filterName: string; filterValue?: string; filterOptions?: LabelValue[]};

export type ExtraFilterType = {
  paramName: string;
  options: LabelValue[];
  placeholder: string;
};

const cardBrandExtraFilter = {
  paramName: 'paymentMethodCardBrand',
  options: [
    {value: 'all', label: 'All'},
    {value: CardBrand.VISA, label: cardBrandLabels.visa},
    {value: CardBrand.MASTERCARD, label: cardBrandLabels.mastercard},
    {value: CardBrand.DISCOVER, label: cardBrandLabels.discover},
    {value: CardBrand.DINERS, label: cardBrandLabels.diners},
    {value: CardBrand.JCB, label: cardBrandLabels.jcb},
    {value: CardBrand.UNIONPAY, label: cardBrandLabels.unionpay},
    {value: CardBrand.AMEX, label: cardBrandLabels.amex}
  ],
  placeholder: 'Select card brand'
};
export const paymentMethodExtraFilter: Record<PaymentMethods, Maybe<ExtraFilterType>> = {
  [PaymentMethods.BIZUM]: {
    paramName: 'paymentMethodBizumIntegrationType',
    options: [
      {value: 'all', label: 'All'},
      {value: 'REST', label: 'REST'},
      {value: 'INSITE', label: 'INSITE'}
    ],
    placeholder: 'Select integration type'
  },
  [PaymentMethods.CARD]: cardBrandExtraFilter,
  [PaymentMethods.APPLEPAY]: cardBrandExtraFilter,
  [PaymentMethods.BANCONTACT]: null,
  [PaymentMethods.CLICKTOPAY]: {
    paramName: 'paymentMethodCardBrand',
    options: [
      {value: 'all', label: 'All'},
      {value: CardBrand.VISA, label: cardBrandLabels.visa},
      {value: CardBrand.MASTERCARD, label: cardBrandLabels.mastercard},
      {value: CardBrand.DISCOVER, label: cardBrandLabels.discover}
    ],
    placeholder: 'Select card brand'
  },
  [PaymentMethods.GIROPAY]: null,
  [PaymentMethods.GOOGLEPAY]: cardBrandExtraFilter,
  [PaymentMethods.IDEAL]: null,
  [PaymentMethods.KLARNA]: null,
  [PaymentMethods.MBWAY]: null,
  [PaymentMethods.MULTIBANCO]: null,
  [PaymentMethods.PAYPAL]: null,
  [PaymentMethods.SOFORT]: null,
  [PaymentMethods.TRUSTLY]: null,
  [PaymentMethods.BLIK]: null,
  [PaymentMethods.EPS]: null,
  [PaymentMethods.SEPA]: null,
  [PaymentMethods.SRTP]: null,
  [PaymentMethods.ALIPAY]: null
};

const amountOperatorOptions = [
  {label: '=', value: 'eq'},
  {label: '>', value: 'gt'},
  {label: '>=', value: 'gte'},
  {label: '<', value: 'lt'},
  {label: '<=', value: 'lte'}
];

export const FilterInput: React.FC<Props> = ({filterName, filterValue, filterOptions}) => {
  const {isoCountries} = useCountries();
  const {isAdmin} = useAccessLevel();
  const {data: chargeSourcesData, loading: loadingChargeSources} = useChargeSourcesQuery({
    skip: !isAdmin
  });

  const chargeSources = chargeSourcesData?.chargeSources || [];
  const name = 'filterValue';
  const extraFilter = paymentMethodExtraFilter[filterValue as PaymentMethods];

  switch (filterName) {
    case 'paymentMethodMethod':
      return (
        <AntInput.Group compact>
          <Select
            name={name}
            style={{flex: '1'}}
            placeholder="Select payment method"
            showSearch
            optionFilterProp="children"
            allowClear>
            {Object.values(PaymentMethods)
              .filter((m) => paymentMethodLabels[m])
              .map((method) => (
                <Select.Option key={method} value={method}>
                  {paymentMethodLabels[method]}
                </Select.Option>
              ))}
          </Select>
          {!!extraFilter && (
            <Select
              name={extraFilter.paramName}
              style={{flex: '1'}}
              placeholder={extraFilter.placeholder}
              options={extraFilter.options}
            />
          )}
        </AntInput.Group>
      );
    case 'paymentMethodCardTokenizationMethod':
      return (
        <AntInput.Group compact>
          <Select
            name={name}
            style={{flex: '1'}}
            placeholder="Select digital wallet"
            showSearch
            optionFilterProp="children"
            allowClear>
            {Object.values(TokenizationMethods)
              .filter((m) => tokenizationMethodLabels[m])
              .map((method) => (
                <Select.Option key={method} value={method}>
                  {tokenizationMethodLabels[method]}
                </Select.Option>
              ))}
          </Select>
          {!!extraFilter && (
            <Select
              name={extraFilter.paramName}
              style={{flex: '1'}}
              placeholder={extraFilter.placeholder}
              options={extraFilter.options}
            />
          )}
        </AntInput.Group>
      );
    case 'acquirer':
      return (
        <Select
          name={name}
          style={{width: '100%'}}
          placeholder="Select acquirer"
          allowClear
          optionFilterProp="children"
          showSearch>
          {enumKeys(Acquirer).map((method) => (
            <Select.Option key={method} value={method}>
              {acquirerLabels[method]}
            </Select.Option>
          ))}
        </Select>
      );
    case 'currency':
      return (
        <Select
          name={name}
          style={{width: '100%'}}
          placeholder="Select currency"
          allowClear
          optionFilterProp="children"
          showSearch>
          {Object.values(Currencies).map((currency) => (
            <Select.Option key={currency} value={currency}>
              {currency}
            </Select.Option>
          ))}
        </Select>
      );
    case 'processor':
      return (
        <Select
          name={name}
          style={{width: '100%'}}
          placeholder="Select payment processor"
          allowClear
          optionFilterProp="children"
          showSearch>
          {enumKeys(Processor).map((method) => (
            <Select.Option key={method} value={method}>
              {paymentProcessorLabels[method]}
            </Select.Option>
          ))}
        </Select>
      );
    case 'shopCountry':
      return (
        <Select
          name={name}
          showSearch
          style={{width: '100%'}}
          optionFilterProp="children"
          placeholder="Select store country"
          allowClear>
          {isoCountries.map((country) => (
            <Select.Option key={country.code} value={country.code}>
              {country.name}
            </Select.Option>
          ))}
        </Select>
      );
    case 'traceDetailsSource':
      return (
        <Select
          name={name}
          style={{width: '100%'}}
          placeholder="Select payment source"
          showSearch
          optionFilterProp="children"
          loading={loadingChargeSources}
          allowClear>
          {chargeSources.map((source) => (
            <Select.Option key={source} value={source}>
              {source}
            </Select.Option>
          ))}
        </Select>
      );
    case 'customerPhone':
      return <PhoneNumberInput name={name} style={{width: '100%'}} allowClear allowEmpty />;
    case 'storeId':
      return (
        <>
          {filterOptions && (
            <Select
              name={name}
              style={{width: '100%'}}
              allowClear
              showSearch
              placeholder={'Select store'}>
              {filterOptions.map((option) => {
                return (
                  <Select.Option value={option.value} key={option.value}>
                    {option.label}
                  </Select.Option>
                );
              })}
            </Select>
          )}
        </>
      );
    case 'masterAccountId':
      return <SelectMasterAccounts name={name} />;
    case 'billingPlan':
      return (
        <Select
          name={name}
          style={{width: '100%'}}
          placeholder="Select billing plan"
          allowClear
          optionFilterProp="children"
          showSearch>
          {enumKeys(billingPlans).map((plan) => (
            <Select.Option key={plan} value={plan}>
              {billingPlans[plan]}
            </Select.Option>
          ))}
        </Select>
      );
    case 'amount':
      return (
        <AntInput.Group compact>
          <Select
            name="amountOperator"
            style={{flex: '1'}}
            placeholder="Select operator"
            options={amountOperatorOptions}
          />
          <InputNumber name={name} precision={2} style={{flex: '3'}} placeholder="Select amount" />
        </AntInput.Group>
      );
    case 'interval': {
      return (
        <Select
          style={{width: '100%'}}
          name={name}
          allowClear
          showSearch
          optionFilterProp="children"
          placeholder="Select interval">
          {filterOptions?.map((option) => {
            return (
              <Select.Option value={option.value} key={option.value}>
                {option.label}
              </Select.Option>
            );
          })}
        </Select>
      );
    }
  }
  return <Input name={name} allowClear />;
};
